import { useNavigator } from '@repo/toolkit/use-navigator'
import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import { EyeOpenIcon, SeatIcon, TravelFolderIcon, TravelTwoIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { updatedAllocatedPlaces } from '../../../api/trip-dates/update-allocated-places'
import { createTripFolder } from '../../../api/trip-folder/create-trip-folder'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import { countFilters } from '../../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../../components/elements/nav-bar'
import { validateSelectedTripDates } from '../../../components/pages/trip-bookings/utils'
import {
  getTripDateColumns,
  tripDateVariantMapping,
  TripDateVariantType
} from '../../../components/pages/trip-dates/trip-date.utils'
import { TripDateBookingToCenterForm } from '../../../components/pages/trip-dates/trip-date-booking-to-center-form'
import { PaxMessageBadge } from '../../../components/pages/trip-dates/trip-dates.ui'
import { TripDatesFilter } from '../../../components/pages/trip-dates/trip-dates-filter'
import TripFolderCreation from '../../../components/pages/trip-dates/trip-folder-creation'
import { Paths } from '../../../lib/constants/path'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { tripTypeMapping } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripDate, TripDateFilter } from '../../../lib/types/models/trip-booking/trip-date'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformTripDatesFilterToUrl,
  transformUrlToTripDatesFilter
} from '../../../lib/utils/filters-mapping/trip-dates'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

export type TripDateListProps = {
  variant: TripDateVariantType
}

export function TripDateList({ variant }: TripDateListProps) {
  const { navigateWithEvent } = useNavigator()
  const queryClient = useQueryClient()
  const tableRef = useRef<DynamicTableRef<TripDate>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)

  const {
    modalState,
    toggleModal,
    closeModel,
    selectedData: tripDateId
  } = useModalManager<'filters' | 'display-trip', number>(['filters', 'display-trip'])

  const travelFileModal = useModalManager<'create-travel-file', TripDate[]>(['create-travel-file'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<TripDateFilter>({
      initialFilters: {
        ...(variant === 'PRIMARY' && { includesAllocations: true }),
        ...(variant === 'SECONDARY' && { includesReservation: true }),
        tripType: tripDateVariantMapping[variant].type
      },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformTripDatesFilterToUrl,
      transformUrlToFilter: transformUrlToTripDatesFilter
    })

  const columns: Column<TripDate>[] = [
    ...getTripDateColumns<TripDate>(variant),
    {
      accessorKey: 'id',
      header: 'Dispatcher les pax',
      cell: ({ row: { original: tripDate } }) => {
        return (
          <div className={'flex flex-col gap-2 justify-center items-center py-2'}>
            {(tripDate.numberOfStudentsPlacesToDispatch > 0 ||
              tripDate.numberOfStaffPlacesToDispatch > 0) && (
              <PaxMessageBadge
                suffix={'à dispatcher'}
                numberOfStaff={tripDate.numberOfStaffPlacesToDispatch}
                numberOfStudents={tripDate.numberOfStudentsPlacesToDispatch}
              />
            )}

            {!tripDate.isComplete && (
              <Button
                icon={EyeOpenIcon}
                label={'Voir la répartition'}
                onClick={(e) => {
                  e.stopPropagation()
                  toggleModal('display-trip', tripDate.id)
                }}
              />
            )}
          </div>
        )
      }
    }
  ]

  const tripFolder = useMutation({
    mutationFn: createTripFolder,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.clearSelection()
      tableRef.current?.refetch()
      travelFileModal.closeModel('create-travel-file')
      toast.success('Dossier créé avec succès')
    }
  })

  const { mutateAsync } = useMutation({
    mutationFn: updatedAllocatedPlaces,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip-date', tripDateId] })
      toast.success('Places réparties avec succès')
      tableRef.current?.refetch()
      closeModel('display-trip')
    }
  })

  useEffect(() => {
    // reset the filters when the variant changes since the filters state is not changing when the initial filters are changed
    resetFilters()
  }, [variant])

  return (
    <div key={variant}>
      <UserNavBar title={tripDateVariantMapping[variant].title} icon={TravelTwoIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<TripDate, TripDateFilter>
        selectable
        disableSelectAll
        ref={tableRef}
        mode={'normal'}
        title={tripDateVariantMapping[variant].tableTitle}
        endpoint={API.tripDates.getAll.url}
        columns={columns}
        allowSearchParam
        version={'FULL'}
        filters={selectedFilter}
        sorting={{ id: 'date', desc: false }}
        filtersLength={countFilters(selectedFilter, [
          'includesAllocations',
          'includesReservation',
          'tripType'
        ])}
        disableRow={(row) => {
          const hasTripFolder = row.tripBookings.every((b) => {
            const tripFolder = b.tripFolders?.find((f) => f.directionality === row.directionality)
            return !!tripFolder
          })
          if (hasTripFolder) return true

          // creating a trip folder requires every dispatched center to be locked
          if (row.dispatchedCenters?.some((center) => !center.locked)) return true

          return false
        }}
        onFiltersClick={() => toggleModal('filters')}
        onRowClick={(tripDate, event) =>
          navigateWithEvent(
            Paths.TRIP_DATES_UPDATE_PAGE.replace(':id', tripDate.id.toString()),
            event
          )
        }
        onLoadingChange={setIsTableLoading}
        permissions={{ delete: false }}
        actions={[
          {
            variant: 'primary',
            type: 'multiple',
            label: 'Créer un dossier voyage',
            fn: (selectedTripDates) => {
              const { valid, error } = validateSelectedTripDates(selectedTripDates)
              if (!valid) {
                toast.error(error)
                return
              }

              travelFileModal.toggleModal('create-travel-file', selectedTripDates)
            }
          }
        ]}
      />

      <FilterDrawer<TripDateFilter>
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <TripDatesFilter
          type={'full'}
          filter={filter}
          tripType={tripTypeMapping[variant].tripType}
          onFilterChange={onFilterChange}
        />
      </FilterDrawer>

      {tripDateId && (
        <Modal
          hideActions
          size={'large'}
          icon={SeatIcon}
          title={'Répartition des places'}
          isOpen={modalState['display-trip']}
          onCancel={() => closeModel('display-trip')}
        >
          <TripDateBookingToCenterForm
            tripDateId={tripDateId}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('display-trip'),
              onConfirm: ({ data }) => mutateAsync({ id: tripDateId, data })
            })}
          />
        </Modal>
      )}

      {travelFileModal.selectedData && (
        <Modal
          hideActions
          size={'medium'}
          icon={TravelFolderIcon}
          title={'Créer un dossier voyage'}
          isOpen={travelFileModal.modalState['create-travel-file']}
          onCancel={() => travelFileModal.closeModel('create-travel-file')}
        >
          <TripFolderCreation
            tripDates={travelFileModal.selectedData}
            isLoading={tripFolder.isPending}
            onCancel={() => travelFileModal.closeModel('create-travel-file')}
            onSubmit={async (tripBookingIds) => {
              const tripDateIds = travelFileModal.selectedData?.map((tripDate) => tripDate.id)
              if (!tripDateIds) return
              await tripFolder.mutateAsync({ tripBookingIds, tripDateIds })
            }}
          />
        </Modal>
      )}
    </div>
  )
}
